import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { listAccounts, listPendingAccounts } from "../../api/accountAdministrationApi";
import { readAccessList } from "../../api/access/accessApi";
import { listInvitations } from "../../api/invitationsApi";
import { groupNamer, RequestsToDecide } from "../access";
import { useLoad } from "../../shared/loading/useLoad";
import AppButton from "../../ui/buttons/AppButton";
import LoadStatus from "../../ui/feedback/LoadStatus";
import AccountsTable from "./accounts/AccountsTable";
import InvitePeopleDialog from "./accounts/InvitePeopleDialog";

/**
 * `/administration/accounts` (web-app.md WA-8, F133, R3-4, GS-8): inviting people into groups,
 * the requests for installation permissions one may grant, and one table of every account and
 * open invitation, filtered by status, each row with its own actions (Sol critique C9).
 */
export default function AccountsPage() {
  const { t } = useTranslation();
  const [inviting, setInviting] = useState(false);
  const loadPending = useCallback((signal: AbortSignal) => listPendingAccounts(signal), []);
  const { state: pending, reload: reloadPending, refresh: refreshPending } = useLoad(loadPending);
  const loadInvitations = useCallback((signal: AbortSignal) => listInvitations(signal), []);
  const { state: invitations, reload: reloadInvitations, refresh: refreshInvitations } = useLoad(loadInvitations);
  const loadAccounts = useCallback((signal: AbortSignal) => listAccounts(signal), []);
  const { state: accounts, reload: reloadAccounts, refresh: refreshAccounts } = useLoad(loadAccounts);
  const loadGroups = useCallback((signal: AbortSignal) => readAccessList(null, signal), []);
  const { state: groups, reload: reloadGroups, refresh: refreshGroups } = useLoad(loadGroups);
  const changed = () => { refreshPending(); refreshAccounts(); refreshGroups(); };
  return (
    <div className="w-full max-w-4xl space-y-4">
      <div className="flex justify-end">
        <AppButton variant="primary" size="sm" onClick={() => { setInviting(true); }}>{t("access.invite.people")}</AppButton>
      </div>
      {inviting ? <InvitePeopleDialog onInvited={refreshInvitations} onClose={() => { setInviting(false); }} /> : null}
      {groups.status === "loaded" ? (
        <RequestsToDecide projectId={null} namer={groupNamer(t, groups.value)} onGranted={() => { refreshGroups(); refreshAccounts(); }} />
      ) : null}
      <LoadStatus state={pending} loadingLabel={t("common.loading")} onRetry={reloadPending} />
      <LoadStatus state={invitations} loadingLabel={t("common.loading")} onRetry={reloadInvitations} />
      <LoadStatus state={accounts} loadingLabel={t("common.loading")} onRetry={reloadAccounts} />
      <LoadStatus state={groups} loadingLabel={t("common.loading")} onRetry={reloadGroups} />
      {accounts.status === "loaded" && groups.status === "loaded" && pending.status === "loaded" && invitations.status === "loaded" ? (
        <AccountsTable
          accounts={accounts.value}
          pending={pending.value}
          invitations={invitations.value.filter((invitation) => invitation.state === "pending")}
          groups={groups.value.groups.filter((group) => group.system !== "everyone")}
          namer={groupNamer(t, groups.value)}
          onChanged={changed}
          onRevoked={refreshInvitations}
        />
      ) : null}
    </div>
  );
}
